import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Lock, Mail, ArrowRight } from "lucide-react";

export default function PasswordReset() {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (token && password !== confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      if (token) {
        await api.post("/auth/reset-password", { token, password });
        toast.success("Password set. Please sign in.");
        navigate("/login", { replace: true });
      } else {
        await api.post("/auth/forgot-password", { email });
        setSent(true);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 md:p-12 bg-background">
      <div className="w-full max-w-sm animate-rise">
        <div className="mb-8"><Brand /></div>
        <h1 className="font-serif-display text-3xl font-semibold tracking-tight">
          {token ? "Set your password" : "Reset your password"}
        </h1>
        <p className="text-muted-foreground text-sm mt-2 mb-8">
          {token
            ? "Enter a new password with at least eight characters. This also works the first time, for an account created with Google sign-in."
            : "We'll email a secure, one-use link to set or reset your password."}
        </p>

        {sent ? (
          <div className="text-center">
            <p className="text-muted-foreground text-sm">If an account exists for that email, the link is on its way. Check your inbox and spam folder.</p>
            <Link to="/login" className="inline-block mt-5 text-primary hover:underline underline-offset-4 text-sm">Return to sign in</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="password-reset-form">
            {token ? (
              <>
                <div>
                  <Label className="overline text-muted-foreground">New password</Label>
                  <div className="relative mt-1.5">
                    <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={8} maxLength={128} required data-testid="new-password-input" className="pl-9 rounded-none bg-secondary border-border focus-visible:ring-primary/50" placeholder="••••••••" />
                  </div>
                </div>
                <div>
                  <Label className="overline text-muted-foreground">Confirm password</Label>
                  <div className="relative mt-1.5">
                    <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} minLength={8} maxLength={128} required data-testid="confirm-password-input" className="pl-9 rounded-none bg-secondary border-border focus-visible:ring-primary/50" placeholder="••••••••" />
                  </div>
                </div>
              </>
            ) : (
              <div>
                <Label className="overline text-muted-foreground">Email</Label>
                <div className="relative mt-1.5">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="reset-email-input" className="pl-9 rounded-none bg-secondary border-border focus-visible:ring-primary/50" placeholder="you@example.com" />
                </div>
              </div>
            )}
            <Button type="submit" disabled={loading} data-testid="submit-password-reset-btn" className="w-full h-11 rounded-none bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{token ? "Set password" : "Send reset link"}<ArrowRight className="w-4 h-4 ml-2" /></>}
            </Button>
          </form>
        )}

        <p className="text-sm text-muted-foreground mt-6 text-center">
          <Link to="/login" className="text-primary hover:underline underline-offset-4">Back to sign in</Link>
        </p>
      </div>
    </div>
  );
}
